import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import { v4 } from "uuid";
import { useCashRegister } from "../cash-register-context";
import { cashWithdrawalCategoryRepository } from "../cash-withdrawal-category.repository";
import { CashWithdrawalCategory } from "../cash-withdrawal-category.entity";
import { payableBillRepository } from "../../payables/payable-bill.repository";
import { PayableBill } from "../../payables/payable-bill.entity";
import { useModuleAccess } from "../../settings/module-access";

const PAY_BILL_ID = "pay_bill";

type Step = 1 | 2 | 3;

type NewBillDraft = {
  title: string;
  amount: string;
  payeeName: string;
  dueDate: string;
};

const emptyDraft = (): NewBillDraft => ({
  title: "",
  amount: "",
  payeeName: "",
  dueDate: moment().format("YYYY-MM-DD"),
});

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CashWithdrawalDialog({ open, onClose }: Props) {
  const theme = useTheme();
  const { addWithdrawal } = useCashRegister();
  const moduleAccess = useModuleAccess();
  const hasPayables = !moduleAccess.loading && moduleAccess.isEnabled("accounts_payable");

  // Wizard step
  const [step, setStep] = useState<Step>(1);

  // Step 1 — category
  const [categories, setCategories] = useState<CashWithdrawalCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [categoryLabel, setCategoryLabel] = useState<string>("");
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingNewCategory, setSavingNewCategory] = useState(false);

  // Step 2 — bill (only when pay_bill + hasPayables)
  const [pendingBills, setPendingBills] = useState<PayableBill[]>([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [selectedBill, setSelectedBill] = useState<PayableBill | null>(null);
  const [addingNewBill, setAddingNewBill] = useState(false);
  const [newBillDraft, setNewBillDraft] = useState<NewBillDraft>(emptyDraft);
  const [billStepError, setBillStepError] = useState<string | null>(null);
  const [billSearch, setBillSearch] = useState("");
  const [billFilter, setBillFilter] = useState<"all" | "5d" | "15d" | "month">("all");

  // Step 2 — amount + reason (for non-pay_bill or pay_bill without module)
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [amountStepError, setAmountStepError] = useState<string | null>(null);

  // Step 3 — review
  const [markPaid, setMarkPaid] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isPayBill = categoryId === PAY_BILL_ID;

  const filteredBills = useMemo(() => {
    const today = moment().startOf("day");
    let list = pendingBills;
    if (billFilter === "5d") {
      const limit = today.clone().add(5, "days").endOf("day");
      list = list.filter((b) => b.getDueDate().isSameOrBefore(limit));
    } else if (billFilter === "15d") {
      const limit = today.clone().add(15, "days").endOf("day");
      list = list.filter((b) => b.getDueDate().isSameOrBefore(limit));
    } else if (billFilter === "month") {
      list = list.filter((b) => b.getDueDate().isSame(today, "month"));
    }
    if (billSearch.trim()) {
      const q = billSearch.trim().toLowerCase();
      list = list.filter(
        (b) =>
          b.getTitle().toLowerCase().includes(q) ||
          b.getPayeeName().toLowerCase().includes(q)
      );
    }
    return list;
  }, [pendingBills, billFilter, billSearch]);

  const reset = useCallback(() => {
    setStep(1);
    setCategories([]);
    setLoadingCategories(false);
    setCategoryId("");
    setCategoryLabel("");
    setPendingBills([]);
    setLoadingBills(false);
    setSelectedBill(null);
    setAddingNewBill(false);
    setNewBillDraft(emptyDraft());
    setBillStepError(null);
    setAmount("");
    setReason("");
    setAmountStepError(null);
    setMarkPaid(true);
    setSubmitting(false);
    setSubmitError(null);
    setShowNewCategoryForm(false);
    setNewCategoryName("");
    setSavingNewCategory(false);
    setBillSearch("");
    setBillFilter("all");
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose();
  }, [submitting, reset, onClose]);

  // ── Step 1 open: load categories ──────────────────────────────────────────
  const handleOpen = useCallback(() => {
    setLoadingCategories(true);
    cashWithdrawalCategoryRepository
      .listAll()
      .then(setCategories)
      .catch(() => setCategories([]))
      .finally(() => setLoadingCategories(false));
  }, []);

  // ── Step 1: save new category inline ────────────────────────────────────
  const handleSaveNewCategory = useCallback(async () => {
    const title = newCategoryName.trim();
    if (!title) return;
    setSavingNewCategory(true);
    try {
      const cat = new CashWithdrawalCategory(v4(), title);
      await cashWithdrawalCategoryRepository.save(cat);
      setCategories((prev) =>
        [...prev, cat].sort((a, b) => a.getTitle().localeCompare(b.getTitle()))
      );
      setNewCategoryName("");
      setShowNewCategoryForm(false);
    } finally {
      setSavingNewCategory(false);
    }
  }, [newCategoryName]);

  // ── Step 1 → 2: pick a category ──────────────────────────────────────────
  const pickCategory = useCallback((id: string, label: string) => {
    setCategoryId(id);
    setCategoryLabel(label);

    if (id === PAY_BILL_ID) {
      // Carrega sempre, independente de hasPayables — moduleAccess pode ainda estar
      // resolvendo no momento do clique, então não podemos confiar no valor capturado.
      // O passo 2A só renderiza se hasPayables for true na hora do render.
      setLoadingBills(true);
      payableBillRepository
        .listPendingOrderedByDue()
        .then(setPendingBills)
        .catch(() => setPendingBills([]))
        .finally(() => setLoadingBills(false));
    }
    setStep(2);
  }, []);

  // ── Step 2 → 3 (bill path) ───────────────────────────────────────────────
  const advanceFromBillStep = () => {
    setBillStepError(null);
    if (addingNewBill) {
      if (!newBillDraft.title.trim()) {
        setBillStepError("Informe o nome / descrição da conta.");
        return;
      }
      const a = parseFloat(newBillDraft.amount.replace(",", "."));
      if (!newBillDraft.amount.trim() || Number.isNaN(a) || a <= 0) {
        setBillStepError("Informe o valor da conta.");
        return;
      }
    }
    // selectedBill can be null — user may skip linking
    setStep(3);
  };

  // ── Step 2 → 3 (amount path) ─────────────────────────────────────────────
  const advanceFromAmountStep = () => {
    setAmountStepError(null);
    const v = parseFloat(amount.replace(",", ".").trim());
    if (!amount.trim() || Number.isNaN(v) || v <= 0) {
      setAmountStepError("Informe um valor válido maior que zero.");
      return;
    }
    if (!reason.trim()) {
      setAmountStepError("Informe o motivo da retirada.");
      return;
    }
    setStep(3);
  };

  // ── Final submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      let resolvedBillId: string | null = null;
      let finalAmount: number;
      let finalReason: string;

      if (isPayBill && hasPayables) {
        if (addingNewBill) {
          const billAmt = parseFloat(newBillDraft.amount.replace(",", "."));
          const newBill = new PayableBill(
            v4(),
            newBillDraft.title.trim(),
            billAmt,
            moment(newBillDraft.dueDate),
            newBillDraft.payeeName.trim() || "—",
            null,
            null,
            false,
            "none",
            "pending",
            null,
            null,
            null,
            moment(),
            moment()
          );
          if (markPaid) {
            newBill.markPaidFully(moment());
          }
          await payableBillRepository.save(newBill);
          resolvedBillId = newBill.id;
          finalAmount = billAmt;
          finalReason = newBillDraft.title.trim();
        } else if (selectedBill) {
          resolvedBillId = selectedBill.id;
          finalAmount = selectedBill.getAmount();
          finalReason = selectedBill.getTitle();
          if (markPaid) {
            if (selectedBill.getIsRecurring() && selectedBill.getRecurrence() !== "none") {
              selectedBill.registerRecurringPayment(moment());
            } else {
              selectedBill.markPaidFully(moment());
            }
            await payableBillRepository.save(selectedBill);
          }
        } else {
          // User skipped linking
          finalAmount = 0;
          finalReason = "Pagar conta";
        }
      } else {
        finalAmount = parseFloat(amount.replace(",", ".").trim());
        finalReason = reason.trim();
      }

      await addWithdrawal({
        amount: finalAmount,
        reason: finalReason,
        categoryId: categoryId || null,
        categoryLabel: categoryLabel || null,
        payableBillId: resolvedBillId,
      });

      handleClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Não foi possível registrar a retirada.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived values for review ─────────────────────────────────────────────
  const reviewAmount: number | null = (() => {
    if (isPayBill && hasPayables) {
      if (addingNewBill) {
        const v = parseFloat(newBillDraft.amount.replace(",", "."));
        return Number.isNaN(v) ? null : v;
      }
      return selectedBill?.getAmount() ?? null;
    }
    const v = parseFloat(amount.replace(",", ".").trim());
    return Number.isNaN(v) ? null : v;
  })();

  const reviewDescription = (() => {
    if (isPayBill && hasPayables) {
      if (addingNewBill) return newBillDraft.title || "—";
      return selectedBill?.getTitle() ?? "—";
    }
    return reason || "—";
  })();

  const stepTitle = ["Categoria", "Detalhe", "Revisão"][step - 1];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: handleOpen }}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={800}>
          Retirada do caixa
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          {([1, 2, 3] as const).map((s) => (
            <Box
              key={s}
              sx={{
                height: 4,
                flex: 1,
                borderRadius: 99,
                bgcolor: step >= s ? "error.main" : "divider",
                transition: "background-color 0.2s",
              }}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Passo {step} de 3 — {stepTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>

        {/* ════════════════════════════════════════
            STEP 1 — Escolher categoria
        ════════════════════════════════════════ */}
        {step === 1 && (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Selecione a categoria da retirada para continuar.
            </Typography>

            {/* Sem categoria */}
            <CategoryCard
              label="Sem categoria"
              sublabel="Retirada avulsa sem categorizar"
              icon={<LabelOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />}
              onClick={() => pickCategory("", "Sem categoria")}
            />

            {/* Pagar conta (fixo) */}
            <CategoryCard
              label="Pagar conta"
              icon={<ReceiptLongIcon fontSize="small" color="primary" />}
              onClick={() => pickCategory(PAY_BILL_ID, "Pagar conta")}
            />

            {loadingCategories ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Carregando categorias…</Typography>
              </Stack>
            ) : (
              categories.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  label={cat.getTitle()}
                  icon={<LabelOutlinedIcon fontSize="small" color="primary" />}
                  onClick={() => pickCategory(cat.id, cat.getTitle())}
                />
              ))
            )}

            {/* Adicionar nova categoria inline */}
            {showNewCategoryForm ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ pt: 0.5 }}
              >
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  placeholder="Nome da nova categoria…"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSaveNewCategory();
                    if (e.key === "Escape") { setShowNewCategoryForm(false); setNewCategoryName(""); }
                  }}
                  disabled={savingNewCategory}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => void handleSaveNewCategory()}
                  disabled={savingNewCategory || !newCategoryName.trim()}
                  sx={{ fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {savingNewCategory ? <CircularProgress size={14} color="inherit" /> : "Salvar"}
                </Button>
                <Button
                  size="small"
                  onClick={() => { setShowNewCategoryForm(false); setNewCategoryName(""); }}
                  disabled={savingNewCategory}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={() => setShowNewCategoryForm(true)}
                sx={{ textTransform: "none", alignSelf: "flex-start", color: "text.secondary" }}
              >
                Nova categoria
              </Button>
            )}
          </Stack>
        )}

        {/* ════════════════════════════════════════
            STEP 2A — Pay bill + hasPayables: selecionar / criar conta
        ════════════════════════════════════════ */}
        {step === 2 && isPayBill && hasPayables && (
          <Stack spacing={2}>
            {loadingBills ? (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Carregando contas…</Typography>
              </Stack>
            ) : (
              <>
                {!addingNewBill && (
                  <>
                    {/* Busca */}
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      placeholder="Buscar por nome ou fornecedor…"
                      value={billSearch}
                      onChange={(e) => setBillSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Filtros */}
                    <ToggleButtonGroup
                      value={billFilter}
                      exclusive
                      onChange={(_, v) => { if (v) setBillFilter(v); }}
                      size="small"
                      sx={{ flexWrap: "wrap", gap: 0.5 }}
                    >
                      {[
                        { value: "all", label: "Todas" },
                        { value: "5d", label: "Próx. 5 dias" },
                        { value: "15d", label: "Próx. 15 dias" },
                        { value: "month", label: "Este mês" },
                      ].map((opt) => (
                        <ToggleButton
                          key={opt.value}
                          value={opt.value}
                          sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5, py: 0.5 }}
                        >
                          {opt.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    {/* Lista */}
                    <Stack
                      spacing={1}
                      sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}
                    >
                      {filteredBills.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          {pendingBills.length === 0
                            ? "Nenhuma conta pendente cadastrada."
                            : "Nenhuma conta encontrada para esse filtro."}
                        </Typography>
                      ) : (
                        filteredBills.map((bill) => (
                          <BillCard
                            key={bill.id}
                            bill={bill}
                            selected={selectedBill?.id === bill.id}
                            onClick={() => setSelectedBill((prev) => prev?.id === bill.id ? null : bill)}
                          />
                        ))
                      )}
                    </Stack>
                  </>
                )}

                {/* Toggle: criar nova conta */}
                <Button
                  size="small"
                  variant={addingNewBill ? "outlined" : "text"}
                  startIcon={addingNewBill ? undefined : <AddCircleOutlineIcon fontSize="small" />}
                  onClick={() => {
                    setAddingNewBill((v) => !v);
                    setSelectedBill(null);
                    setBillStepError(null);
                  }}
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  {addingNewBill ? "Cancelar — usar conta existente" : "Criar nova conta"}
                </Button>

                {addingNewBill && (
                  <Stack spacing={1.5} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="subtitle2" fontWeight={700}>Nova conta a pagar</Typography>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      label="Nome / descrição da conta"
                      value={newBillDraft.title}
                      onChange={(e) => setNewBillDraft((d) => ({ ...d, title: e.target.value }))}
                    />
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Valor (R$)"
                        type="number"
                        inputProps={{ min: 0.01, step: 0.01 }}
                        value={newBillDraft.amount}
                        onChange={(e) => setNewBillDraft((d) => ({ ...d, amount: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Vencimento"
                        type="date"
                        value={newBillDraft.dueDate}
                        onChange={(e) => setNewBillDraft((d) => ({ ...d, dueDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fornecedor / beneficiário (opcional)"
                      value={newBillDraft.payeeName}
                      onChange={(e) => setNewBillDraft((d) => ({ ...d, payeeName: e.target.value }))}
                    />
                  </Stack>
                )}
              </>
            )}

            {billStepError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{billStepError}</Alert>
            )}
          </Stack>
        )}

        {/* ════════════════════════════════════════
            STEP 2B — Pay bill + !hasPayables  OR  any other category
            → inserir valor e descrição manualmente
        ════════════════════════════════════════ */}
        {step === 2 && (!isPayBill || !hasPayables) && (
          <Stack spacing={2.5}>
            {/* Banner promocional compacto quando pay_bill sem módulo */}
            {isPayBill && !hasPayables && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.warning.main, 0.4),
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <StarOutlineIcon fontSize="small" sx={{ color: "warning.dark", mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="warning.dark">
                    Módulo de Contas a Pagar não ativo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ative para vincular contas, controlar vencimentos e marcar pagamentos automaticamente.
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      href="/meu-plano"
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, py: 0.25 }}
                    >
                      Ver planos
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            <TextField
              autoFocus
              fullWidth
              label="Valor retirado (R$)"
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              fullWidth
              label="Motivo / descrição"
              placeholder="Descreva o motivo da retirada…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />

            {amountStepError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{amountStepError}</Alert>
            )}
          </Stack>
        )}

        {/* ════════════════════════════════════════
            STEP 3 — Revisão
        ════════════════════════════════════════ */}
        {step === 3 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Confira os dados antes de confirmar.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: (t) => alpha(t.palette.error.main, 0.03),
              }}
            >
              <Stack spacing={1.25}>
                <ReviewRow label="Categoria" value={categoryLabel || "Sem categoria"} />
                <Divider />
                <ReviewRow
                  label="Valor"
                  value={
                    reviewAmount != null
                      ? reviewAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                      : "—"
                  }
                  highlight
                />
                <Divider />
                <ReviewRow label="Descrição" value={reviewDescription} />
                {isPayBill && hasPayables && (selectedBill || addingNewBill) && (
                  <>
                    <Divider />
                    <ReviewRow
                      label="Conta vinculada"
                      value={addingNewBill ? `Nova: ${newBillDraft.title || "—"}` : (selectedBill?.getTitle() ?? "—")}
                    />
                  </>
                )}
              </Stack>
            </Box>

            {isPayBill && hasPayables && (selectedBill || addingNewBill) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={markPaid}
                    onChange={(e) => setMarkPaid(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Marcar a conta como paga no módulo de Contas a Pagar
                  </Typography>
                }
              />
            )}

            {submitError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>
            )}
          </Stack>
        )}

      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {step === 1 ? (
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
        ) : (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => { setStep((s) => (s - 1) as Step); setSubmitError(null); }}
            disabled={submitting}
            sx={{ textTransform: "none" }}
          >
            Voltar
          </Button>
        )}

        {/* Step 2 advance buttons */}
        {step === 2 && isPayBill && hasPayables && (
          <Button
            variant="contained"
            onClick={advanceFromBillStep}
            disabled={loadingBills}
            sx={{ fontWeight: 800 }}
          >
            {selectedBill || addingNewBill ? "Próximo" : "Pular e continuar"}
          </Button>
        )}

        {step === 2 && (!isPayBill || !hasPayables) && (
          <Button
            variant="contained"
            onClick={advanceFromAmountStep}
            sx={{ fontWeight: 800 }}
          >
            Próximo
          </Button>
        )}

        {step === 3 && (
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{ fontWeight: 800 }}
          >
            {submitting ? "Salvando…" : "Confirmar retirada"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

type CategoryCardProps = {
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  highlight?: boolean;
  onClick: () => void;
};

function CategoryCard({ label, sublabel, icon, highlight, onClick }: CategoryCardProps) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: highlight ? alpha(theme.palette.error.main, 0.3) : "divider",
        borderRadius: 2,
        bgcolor: highlight ? alpha(theme.palette.error.main, 0.03) : "transparent",
      }}
    >
      <CardActionArea onClick={onClick} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ flexShrink: 0 }}>{icon}</Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color={highlight ? "error.main" : "text.primary"}>
                {label}
              </Typography>
              {sublabel && (
                <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

type BillCardProps = {
  bill: PayableBill;
  selected: boolean;
  onClick: () => void;
};

function BillCard({ bill, selected, onClick }: BillCardProps) {
  const theme = useTheme();
  const overdue = bill.isOverdue();
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: selected
          ? theme.palette.primary.main
          : overdue
          ? alpha(theme.palette.error.main, 0.3)
          : "divider",
        borderRadius: 2,
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.05) : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
      }}
    >
      <CardActionArea onClick={onClick} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ReceiptLongIcon
              fontSize="small"
              sx={{ color: selected ? "primary.main" : "text.secondary", flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {bill.getTitle()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bill.getPayeeName()} · vence {moment(bill.getDueDate()).format("DD/MM/YYYY")}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
              <Typography variant="body2" fontWeight={800} color={selected ? "primary.main" : "text.primary"}>
                {bill.getAmount().toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </Typography>
              {overdue && <Chip label="Vencida" size="small" color="error" sx={{ height: 18, fontSize: "0.6rem" }} />}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

type ReviewRowProps = { label: string; value: string; highlight?: boolean };

function ReviewRow({ label, value, highlight }: ReviewRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={highlight ? 800 : 600}
        color={highlight ? "error.main" : "text.primary"}
      >
        {value}
      </Typography>
    </Stack>
  );
}
